import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createMtrStructMain, fetchMtrStructMainDetail, updateMtrStructMain } from '#/api/imsMgmt/settings/mtrStruct';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { mtrStructMainFormSchema } from '#/modules/imsMgmt/settings/mtrStruct/schemas/mtrStructSchemas';
import {
  getMtrStructApiErrorMessage,
  isMtrStructVersionConflict,
} from '#/modules/imsMgmt/settings/mtrStruct/helper/apiError';
import { MTR_STRUCT_MAIN_FIELD_LABELS } from '#/modules/imsMgmt/settings/mtrStruct/helper/formValidationLabels';
import {
  mtrStructMainDetailQueryKey,
  mtrStructMainListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditMtrStructMainPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: mtrStructMainDetailQueryKey(id),
    queryFn: () => fetchMtrStructMainDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createMtrStructMain,
    updateFn: updateMtrStructMain,
    buildPayload: (values) => {
      const payload = {
        code: values.code,
        name: values.name,
        remark: values.remark || null,
      };

      if (id) {
        return {
          ...payload,
          updatedAt: updatedAtRef.current,
        };
      }

      return payload;
    },
    listQueryKeyPrefix: mtrStructMainListQueryKeyPrefix,
    detailQueryKey: mtrStructMainDetailQueryKey,
    getEditPath: ROUTES.mtrStructMainEdit,
    getErrorMessage: (error) => getMtrStructApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isMtrStructVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(mtrStructMainFormSchema),
    defaultValues: {
      code: '',
      name: '',
      remark: '',
    },
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset({
      code: detail.code ?? '',
      name: detail.name ?? '',
      remark: detail.remark ?? '',
    });
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯主分類' : '新增主分類';
  const isPageLoading = isEdit && detailQuery.isLoading;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-lg">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.mtrStruct}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MTR_STRUCT_MAIN_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <FloatingInput
                    id="code"
                    label="代號"
                    register={register}
                    errors={errors}
                  />
                  <FloatingInput
                    id="name"
                    label="名稱"
                    register={register}
                    errors={errors}
                  />
                  <FloatingTextarea
                    id="remark"
                    label="備註"
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtrStruct}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMtrStructMainPage;
